import { canonicalStorageKey, legacy } from './legacy.js'
import {
  assertValidStorageValue,
  canonicalizeStorageValue,
  StorageMigrationError,
  storageValuesEqual,
} from './storage.js'

const canonicalDatabaseName = 'levery'
const objectStoreName = 'store'

export async function migrateLocalStorage(storage: globalThis.Storage) {
  const names = Array.from({ length: storage.length }, (_, index) =>
    storage.key(index),
  ).filter((name): name is string => !!name && !!canonicalStorageKey(name))

  for (const historicalName of names) {
    const canonicalName = canonicalStorageKey(historicalName)!
    await migrateStringEntry({
      canonicalName,
      get: (name) => storage.getItem(name),
      historicalName,
      remove: (name) => storage.removeItem(name),
      set: (name, value) => storage.setItem(name, value),
    })
  }
}

export async function migrateCookies(document: Document) {
  const cookies = new Map(
    document.cookie
      .split('; ')
      .filter(Boolean)
      .map((cookie) => {
        const separator = cookie.indexOf('=')
        return separator === -1
          ? [cookie, '']
          : [cookie.slice(0, separator), cookie.slice(separator + 1)]
      }),
  )

  for (const historicalName of cookies.keys()) {
    const canonicalName = canonicalStorageKey(historicalName)
    if (!canonicalName) continue
    await migrateStringEntry({
      canonicalName,
      get: (name) => readCookie(document, name),
      historicalName,
      remove: (name) => removeCookie(document, name),
      set(name, value) {
        document.cookie = `${name}=${value};path=/;samesite=None;secure;max-age=31536000`
      },
    })
  }
}

export function removeCookie(document: Document, name: string) {
  const domains = cookieDomains(document.location.hostname)
  const paths = cookiePaths(document.location.pathname)
  const expired = 'expires=Thu, 01 Jan 1970 00:00:00 GMT;max-age=0'

  for (const path of paths) {
    document.cookie = `${name}=;${expired};path=${path}`
    for (const domain of domains) {
      document.cookie = `${name}=;${expired};path=${path};domain=${domain}`
    }
  }
}

function cookieDomains(hostname: string) {
  const labels = hostname.split('.').filter(Boolean)
  if (labels.length < 2) return []

  return Array.from(
    new Set(
      labels.slice(0, -1).map((_, index) => labels.slice(index).join('.')),
    ),
  )
}

function cookiePaths(pathname: string) {
  const paths = new Set(['/'])
  let current = ''

  for (const segment of pathname.split('/').filter(Boolean)) {
    current += `/${segment}`
    paths.add(current)
    paths.add(`${current}/`)
  }

  return [...paths]
}

export async function migrateIndexedDb(factory: IDBFactory) {
  const historical = await openExistingDatabase(factory, legacy.databaseName)
  const canonical = await openDatabase(factory, canonicalDatabaseName)

  try {
    const historicalEntries = historical ? await getAllEntries(historical) : []
    const directEntries = historicalEntries.filter(
      ([key]) => typeof key !== 'string' || !canonicalStorageKey(key),
    )
    const aliasedEntries = historicalEntries.filter(
      ([key]) => typeof key === 'string' && !!canonicalStorageKey(key),
    )

    for (const [historicalKey, historicalValue] of [
      ...directEntries,
      ...aliasedEntries,
    ]) {
      const canonicalKey =
        typeof historicalKey === 'string'
          ? (canonicalStorageKey(historicalKey) ?? historicalKey)
          : historicalKey
      const existing = await getValue(canonical, canonicalKey)
      if (existing.found) {
        if (typeof canonicalKey === 'string')
          assertValidStorageValue(canonicalKey, existing.value)
        continue
      }

      const canonicalValue = canonicalizeStorageValue(historicalValue)
      if (typeof canonicalKey === 'string')
        assertValidStorageValue(canonicalKey, canonicalValue)
      await putValue(canonical, canonicalKey, canonicalValue)
      const written = await getValue(canonical, canonicalKey)
      if (!written.found || !storageValuesEqual(written.value, canonicalValue))
        throw new StorageMigrationError()
    }

    const canonicalEntries = await getAllEntries(canonical)
    const aliasesToDelete: IDBValidKey[] = []
    for (const [key] of canonicalEntries) {
      if (typeof key !== 'string') continue
      const canonicalKey = canonicalStorageKey(key)
      if (!canonicalKey) continue
      const current = await getValue(canonical, canonicalKey)
      if (!current.found) throw new StorageMigrationError()
      assertValidStorageValue(canonicalKey, current.value)
      aliasesToDelete.push(key)
    }

    await deleteValues(canonical, aliasesToDelete)
    for (const key of aliasesToDelete)
      if ((await getValue(canonical, key)).found)
        throw new StorageMigrationError()
  } catch (error) {
    throw error instanceof StorageMigrationError
      ? error
      : new StorageMigrationError(error)
  } finally {
    canonical.close()
    historical?.close()
  }

  if (historical) await deleteDatabase(factory, legacy.databaseName)
}

async function migrateStringEntry(parameters: {
  canonicalName: string
  get: (name: string) => string | null
  historicalName: string
  remove: (name: string) => void
  set: (name: string, value: string) => void
}) {
  const { canonicalName, get, historicalName, remove, set } = parameters
  try {
    const historical = get(historicalName)
    if (historical === null) return

    const current = get(canonicalName)
    if (current !== null) validateSerialized(canonicalName, current)
    else {
      const canonical = canonicalizeSerialized(canonicalName, historical)
      set(canonicalName, canonical)
      if (get(canonicalName) !== canonical) throw new StorageMigrationError()
    }

    remove(historicalName)
    if (get(historicalName) !== null) throw new StorageMigrationError()
  } catch (error) {
    throw error instanceof StorageMigrationError
      ? error
      : new StorageMigrationError(error)
  }
}

function canonicalizeSerialized(name: string, value: string) {
  try {
    const parsed = JSON.parse(value)
    const canonical = canonicalizeStorageValue(parsed)
    assertValidStorageValue(name, canonical)
    return JSON.stringify(canonical)
  } catch (error) {
    if (name === '__levery_theme') {
      assertValidStorageValue(name, value)
      return value
    }
    throw error instanceof StorageMigrationError
      ? error
      : new StorageMigrationError(error)
  }
}

function validateSerialized(name: string, value: string) {
  canonicalizeSerialized(name, value)
}

function readCookie(document: Document, name: string) {
  const prefix = `${name}=`
  const cookie = document.cookie
    .split('; ')
    .find((value) => value.startsWith(prefix))
  return cookie?.slice(prefix.length) ?? null
}

function openExistingDatabase(factory: IDBFactory, name: string) {
  return new Promise<IDBDatabase | null>((resolve, reject) => {
    const request = factory.open(name)
    let missing = false
    request.onupgradeneeded = (event) => {
      missing = event.oldVersion === 0
      if (missing) request.transaction?.abort()
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => {
      if (missing && request.error?.name === 'AbortError') resolve(null)
      else reject(request.error)
    }
  })
}

function openDatabase(factory: IDBFactory, name: string) {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = factory.open(name)
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(objectStoreName))
        request.result.createObjectStore(objectStoreName)
    }
    request.onsuccess = () => {
      if (!request.result.objectStoreNames.contains(objectStoreName)) {
        request.result.close()
        reject(new StorageMigrationError())
        return
      }
      resolve(request.result)
    }
    request.onerror = () => reject(request.error)
  })
}

function getAllEntries(database: IDBDatabase) {
  return transaction(database, 'readonly', async (store) => {
    const [keys, values] = await Promise.all([
      request(store.getAllKeys()),
      request(store.getAll()),
    ])
    return keys.map((key, index) => [key, values[index]] as const)
  })
}

function getValue(database: IDBDatabase, key: IDBValidKey) {
  return transaction(database, 'readonly', async (store) => {
    const value = await request(store.get(key))
    return value === undefined
      ? ({ found: false } as const)
      : ({ found: true, value } as const)
  })
}

function putValue(database: IDBDatabase, key: IDBValidKey, value: unknown) {
  return transaction(database, 'readwrite', (store) => {
    store.put(value, key)
  })
}

function deleteValues(database: IDBDatabase, keys: readonly IDBValidKey[]) {
  if (keys.length === 0) return Promise.resolve()
  return transaction(database, 'readwrite', (store) => {
    for (const key of keys) store.delete(key)
  })
}

function transaction<value>(
  database: IDBDatabase,
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => value | Promise<value>,
) {
  return new Promise<value>((resolve, reject) => {
    const tx = database.transaction(objectStoreName, mode)
    let result: value | Promise<value>
    try {
      result = operation(tx.objectStore(objectStoreName))
    } catch (error) {
      tx.abort()
      reject(error)
      return
    }
    tx.oncomplete = () => Promise.resolve(result).then(resolve, reject)
    tx.onabort = tx.onerror = () => reject(tx.error)
  })
}

function request<value>(operation: IDBRequest<value>) {
  return new Promise<value>((resolve, reject) => {
    operation.onsuccess = () => resolve(operation.result)
    operation.onerror = () => reject(operation.error)
  })
}

function deleteDatabase(factory: IDBFactory, name: string) {
  return new Promise<void>((resolve, reject) => {
    const request = factory.deleteDatabase(name)
    request.onsuccess = () => resolve()
    request.onerror = () => reject(new StorageMigrationError(request.error))
    request.onblocked = () => reject(new StorageMigrationError())
  })
}
